import React, { PureComponent } from 'react';
import {
  SimpleForm,
  SimpleShowLayout,
  ImageInput,
  TextInput,
  CheckboxGroupInput,
  BooleanInput,
  NumberInput,
  FormDataConsumer,
  required,
  useNotify,
  useRefresh,
} from 'react-admin';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { withStyles } from '@mui/styles';
import ImgField from '../../../components/ImgField/ImgField';
import SessionExercises from '../SessionExercises/SessionExercises';
import uploadPicture from '../../../utils/uploadPicture';
import ImagePickerButton from '../../../components/ImagePickerButton/ImagePickerButton';
import VideoFileInput from '../../../components/VideoFileInput';
import SessionDetailToolbar from './SessionDetailToolbar';
import durationOptions from '../../../constants/durationOptions';
import normalizeDurationOptions from '../normalizeDurationOptions';
import Loading from '../../../components/Loading/Loading';
import dataProvider from '../../../providers/dataProvider';
import normalizeBullets from '../../../utils/normalizeBullets';
import VideoPickerButton, { SelectedVideoDisplay } from '../../../components/VideoPickerButton';

const styles = {
  simpleShowLayout: {
    width: '100%',
    marginBottom: '20px',
    background: 'white',
    boxShadow:
      '0px 1px 3px 0px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 2px 1px -1px rgba(0, 0, 0, 0.12)',
    '& .MuiFormControl-root': {
      width: '100%',
    },
  },
  simpleShowLayoutTitle: {
    paddingLeft: '0',
  },
  linkBack: {
    margin: '10px 0 0px 25px',
    display: 'block',
  },
};

class SessionDetailForm extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      selectedLibraryVideo: null,
      videoUploading: false,
    };
  }

  handleVideoUploadingChange = (videoUploading) => {
    this.setState({ videoUploading });
  };

  uploadImg = (img) => uploadPicture(img);

  resolveIntroVideoId = (introScreen) => {
    const video = introScreen && introScreen.video;
    if (this.state.selectedLibraryVideo) {
      return this.state.selectedLibraryVideo.id;
    }
    if (video && video.id) {
      return video.id;
    }
    return null;
  };

  isIntroScreen = (introScreen) => {
    if (!introScreen) {
      return false;
    }
    return !!Object.values(introScreen).filter((item) => item).length;
  };

  handleLibraryVideoSelect = (video) => {
    this.setState({ selectedLibraryVideo: video });
  };

  handleLibraryVideoClear = () => {
    this.setState({ selectedLibraryVideo: null });
  };

  save = (values) => {
    const data = { ...values };
    data.bullets = normalizeBullets(data.bullets);
    this.setState(() => ({ loading: true }));

    // Apply library video selection to intro screen if set
    if (this.state.selectedLibraryVideo && data.introScreen) {
      data.introScreen.video = this.state.selectedLibraryVideo;
    }

    if (!this.isIntroScreen(data.introScreen)) {
      data.introScreen = null;
    }
    // Keep the original picture if no new picture was provided
    const originalPicture = this.props.record?.picture;
    const sessionId = values.id;
    const introVideoId = this.resolveIntroVideoId(data.introScreen);

    Promise.resolve(this.uploadImg(data.picture))
      .then((res) => {
        data.picture = res.data.fileName || originalPicture;
        data.durationOptions = normalizeDurationOptions(data.durationOptions);
        if (data.introScreen) {
          data.introScreen.videoId = introVideoId;
          // Strip the video object — only videoId is needed by the backend
          const { video, ...cleanedIntroScreen } = data.introScreen;
          data.introScreen = {
            ...cleanedIntroScreen,
            title: cleanedIntroScreen.title || '',
            text: cleanedIntroScreen.text || '',
          };
        }

        return dataProvider.update('sessions', { id: sessionId, data });
      })
      .then(() => {
        this.props.notify('Session saved', { type: 'success' });
        this.props.refresh();
      })
      .catch((error) => {
        this.props.notify(error?.message || 'Failed to save session', { type: 'error' });
      })
      .finally(() => {
        this.setState(() => ({ loading: false }));
      });
  };

  render() {
    const { classes, record, notify, refresh, ...props } = this.props;
    const { selectedLibraryVideo, videoUploading } = this.state;
    return (
      <>
        {record?.program ? (
          <Link to={`/programs/${record.program.id}`} className={classes.linkBack}>
            Back to program
          </Link>
        ) : null}
        <Loading open={this.state.loading} />
        <SimpleForm
          {...props}
          record={record}
          onSubmit={this.save}
          toolbar={<SessionDetailToolbar uploading={videoUploading} />}
        >
          <SimpleShowLayout className={classes.simpleShowLayout}>
            <ImageInput
              source="picture"
              label="Session Photo"
              accept={{ 'image/*': [] }}
              format={(v) => (typeof v === 'string' ? { picture: v } : v)}
              validate={required((_) => 'ra.validation.required')}
            >
              <ImgField width="100" height="100" source="picture" />
            </ImageInput>
            <ImagePickerButton source="picture" label={false} />
            <TextInput source="name" label="Session name" validate={required()} />
            <div>Duration Options (in minutes)</div>
            <CheckboxGroupInput
              source="durationOptions"
              validate={required()}
              helperText="Adding a duration adds a column with 0 seconds on every exercise below; fill those in, or the exercise is skipped at that duration."
              format={(v) => {
                if (v && !Array.isArray(v)) {
                  return Object.keys(v);
                }
                return v;
              }}
              label="These are approximate session durations. Final duration will vary depending on the exercises added on each session"
              choices={durationOptions}
            />
            <TextInput source="description" label="Description" validate={required()} />
            <BooleanInput label="Basic Level" source="isBasic" />
            <NumberInput
              label="Challenge Order (1-3, empty = regular session)"
              source="challengeOrder"
              helperText="Must also be placed in each program track it belongs to (Program → Tracks). Only offered for workout durations this session supports."
              min={1}
              step={1}
              parse={(v) => (v === '' || v == null || Number.isNaN(v) ? null : v)}
            />
          </SimpleShowLayout>
          <SimpleShowLayout className={classes.simpleShowLayout}>
            <h4>Introduction Screen</h4>
            <TextInput source="introScreen.title" label="Title" />
            <VideoFileInput
              source="introScreen.video"
              label="Intro video (upload new)"
              values={record}
              checkedName="introScreen.video"
              checkedResource="sessions"
              onUploadingChange={this.handleVideoUploadingChange}
            />
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Or select an existing video from the library:
              </Typography>
              <SelectedVideoDisplay
                video={selectedLibraryVideo}
                onClear={this.handleLibraryVideoClear}
              />
              <VideoPickerButton onSelect={this.handleLibraryVideoSelect} />
            </Box>
            <TextInput source="introScreen.text" label="Text" />
          </SimpleShowLayout>
          <SimpleShowLayout className={classes.simpleShowLayout}>
            <h4>Session Detail Bullet Points</h4>
            <TextInput source="bullets.0" label="Text" />
            <TextInput source="bullets.1" label="Text" />
            <TextInput source="bullets.2" label="Text" />
          </SimpleShowLayout>
          <SimpleShowLayout className={classes.simpleShowLayout}>
            <FormDataConsumer>
              {({ formData }) => {
                return <SessionExercises source="sessionExercise" values={formData} />;
              }}
            </FormDataConsumer>
          </SimpleShowLayout>
        </SimpleForm>
      </>
    );
  }
}

const SessionDetailFormWithNavigation = (props) => {
  const notify = useNotify();
  const refresh = useRefresh();
  return <SessionDetailForm {...props} notify={notify} refresh={refresh} />;
};

export default withStyles(styles)(SessionDetailFormWithNavigation);
